import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  fetchKarteInfo,
  fetchKarteInfoDetail,
  actions,
} from 'slices/karteSlice';
import AprosButton from 'components/aprosButton';
import Linked from 'components/linked';
import Proctitle from 'components/proctitle';
import InputSelect from 'components/inputSelect';
import {
  KARTE_ICONS,
  CAR_IMAGE,
  CAR_IMAGE_SUFFIX,
} from 'constants/karte';
import Path from 'constants/routePath';
import dayjs from 'dayjs';
import Icons from 'images/icons';
import { displayCautionActions } from 'slices/displayCautionSlice';
import { openTirePage } from 'utils/tireRequestUtil';
import KarteDetailModal from './modals/karteDetailModal';
import NotesModal from 'components/messageModals/notesModal';
import {
  KARTE_SELECT_CAR,
  KARTE_SELECT_SERVICE,
  KARTE_SHOW_DETAIL_SERVICE,
} from '../../constants/gtmCustomEvent';
import { Helmet } from 'react-helmet';
import Title from '../../constants/title';

const KarteDescription = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cautionDisplayDate } = useSelector(state => state.displayCaution);

  const {
    karteInfo,
    selectedSrySeq,
    showDetailModal,
    selectedKarteInfo,
    karteInfoDetail,
    tireContractDate,
    tireContractAlert,
  } = useSelector(state => state.karte);

  const { accountInfo, vehicles, isTireContract } = useSelector(state => state.account);
  const { cardNumber, userId } = accountInfo || {};

  const vehicleName = vehicles.find(v => v.carSeq === selectedSrySeq)?.label;

  const [alertGif, setAlertGif] = useState([]);

  const [ref, inView] = useInView();

  const [once, setOnce] = useState(false);

  // アラートの有無に応じて車の画像レイヤーを決定
  const decisionCarImageLayers = (parts) =>
    parts?.filter(part => part.alert === '1')
      ?.map(part => ({
        src: CAR_IMAGE[`car_${CAR_IMAGE_SUFFIX[part.partName]}`],
        partName: part.partName,
      }))
      .filter(layer => layer.src) || [];

  useEffect(() => {
    inView && setOnce(false);
  }, [inView]);

  useEffect(() => {
    if (!karteInfo?.parts) return;
    const alertGif = decisionCarImageLayers(karteInfo.parts);
    setAlertGif(alertGif.map((layer, idx) => ({
      ...layer,
      key: `${layer.partName}-${Date.now()}-${idx}`,
    })));
  }, [karteInfo?.parts, inView]);

  useEffect(() => {
    return () => {
      dispatch(actions.clear());
    };
  }, [dispatch]);

  useEffect(() => {
    // 車両取得時
    vehicles.length > 0 &&
      dispatch(actions.setSelectedSrySeq(vehicles[0].carSeq));
  }, [dispatch, vehicles]);

  useEffect(() => {
    // 車両変更時
    if (selectedSrySeq) {
      setAlertGif([]);
      dispatch(
        fetchKarteInfo({
          karteParams: {
            kaiincrdNo: cardNumber,
            srySeq: selectedSrySeq
          },
          tireStorageParams: {
            userId: userId,
            srySeq: selectedSrySeq
          },
          isTireContract: isTireContract
        })
      );
    }
  }, [dispatch, selectedSrySeq]);

  return (
    <>
      <Helmet>
        <title>{Title.KARTE}</title>
      </Helmet>
      <Proctitle text="車のカルテ" useH1={true} />
      <InputSelect
        title="車種"
        options={vehicles}
        value={selectedSrySeq}
        onChange={v => {
          // GTMカスタムイベントパラメータ
          const dataLayer = (window.dataLayer = window.dataLayer || []);
          dataLayer.push({
            event: KARTE_SELECT_CAR,
          });

          dispatch(actions.setSelectedSrySeq(v));
        }}
        className={{
          boxClass: 'mt-6 p-0 sm:mt-14',
          titleClass: '',
          inputClass: 'sm:ml-8 sm:w-96',
        }}
        isShowErrorArea={false}
      />
      <div className="mt-4 flex flex-col gap-x-8 lg:mt-6 lg:flex-row">
        <div className="flex h-fit flex-1 flex-col border border-line bg-white px-8 py-4 sm:px-8 lg:py-10">
          <div className="sm:px-20 sm:py-10 lg:px-20 lg:py-10">
            <div className="relative w-full">
              <Icons.Image
                src={CAR_IMAGE['car']}
                className="w-full h-auto block"
                alt="車両ベース画像"
                style={{ position: 'relative', zIndex: 1 }}
              />
              {alertGif.map((layer, idx) => {
                return (
                  <Icons.Image
                    key={layer.key}
                    src={layer.src}
                    className="absolute top-0 left-0 w-full h-full pointer-events-none"
                    alt={`アラートレイヤー${idx + 1}`}
                    style={{
                      opacity: 0.9,
                      zIndex: ['夏タイヤ', 'スタッドレスタイヤ'].includes(layer.partName) ? 0 : 2,
                    }}
                  />
                );
              })}
            </div>
          </div>
          <motion.div className="mt-4 sm:mt-0" ref={ref} />
          <div className="flex flex-col sm:items-start lg:items-start">
            <div className="flex flex-col sm:ml-20 lg:ml-20">
              {karteInfo.carInspectionMessage && (
                <div className="flex flex-wrap items-center gap-x-6 mb-2">
                  <label className="text-sm text-red">
                    {karteInfo.carInspectionMessage}
                  </label>
                  <div className="flex gap-x-6">
                    <Linked
                      text="詳細"
                      isLine={true}
                      linkIcon="R"
                      className="text-sm font-bold"
                      onClick={() => {
                        // GTMカスタムイベントパラメータ
                        const dataLayer = (window.dataLayer = window.dataLayer || []);
                        dataLayer.push({
                          event: KARTE_SHOW_DETAIL_SERVICE,
                          karteShowDetailService: `{partId: carInspection, partName: 車検}`,
                        });
                        dispatch(
                          fetchKarteInfoDetail({
                            partId: '01',
                            partName: '車検',
                            cardNumber,
                            selectedSrySeq,
                          })
                        );
                      }}
                    />
                    <Linked
                      text="予約"
                      isLine={true}
                      linkIcon="R"
                      className="text-sm font-bold"
                      onClick={() => {
                        // GTMカスタムイベントパラメータ
                        const dataLayer = (window.dataLayer = window.dataLayer || []);
                        dataLayer.push({
                          event: KARTE_SELECT_SERVICE,
                          karteSelectService: `{chubunCd: '01'}`,
                        });

                        const navToSelect = () => {
                          navigate(Path.RESERVATION_SERVICE_SELECT, {
                            state: {
                              shopCode: accountInfo.shopCode,
                              serviceSelectScreen: Path.KARTE,
                              selectedSrySeq,
                              chubunCd: '003',
                              shobunCd: '',
                            },
                          });
                        };
                        if (!cautionDisplayDate) {
                          dispatch(displayCautionActions.check(navToSelect));
                        } else {
                          navToSelect();
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              {karteInfo.parts && (
                <>
                  <label className="text-sm font-medium">
                    次回車検年月日 {karteInfo.parts.find(part => part.partId === '01')?.replacementRecommendedMonth || '-'}
                  </label>
                  <label className="text-xs font-medium text-navy-light mb-2">
                    前回車検日 {karteInfo.parts.find(part => part.partId === '01')?.replacementDate || '-'}
                  </label>
                </>
              )}
              <label className="text-sm font-medium">
                走行距離 {karteInfo.mileage}
              </label>
              <label className="text-xs font-medium text-navy-light">
                最終入力日 {karteInfo.lastCheckDate}
              </label>
            </div>
          </div>
          {(isTireContract && tireContractDate) && (
            <div className="w-full">
              {!tireContractAlert ? (
                <div className="flex justify-between text-sm mt-4 border-b border-line py-2 mb-1">
                  <label className="text-sm font-bold">
                    タイヤ保管サービス
                  </label>
                  <div>
                    <Linked
                      text={'予約'}
                      onClick={() => openTirePage(cardNumber, null)}
                      isLine
                      linkIcon="R"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex justify-between text-sm mt-4 border-b border-line py-2 mb-1 break-keep">
                  <div className="flex">
                    <Icons.Image
                      src={Icons.alert}
                      className="sm:h-6 sm:w-6 h-5 w-5"
                    />
                    <label className="text-sm font-bold ml-1">
                      タイヤ保管期限が近づいています
                    </label>
                  </div>
                  <div>
                    <Linked
                      text={'予約'}
                      onClick={() => openTirePage(cardNumber, null)}
                      isLine
                      linkIcon="R"
                    />
                  </div>
                </div>
              )}
              <label className="text-sm font-medium break-keep">
                タイヤ保管サービス保管期限　{dayjs(tireContractDate).format('YYYY年M月D日')}
              </label>
            </div>
          )}
        </div>
        <div className="mt-[51px] flex flex-1 flex-col gap-y-11 sm:mt-14 sm:gap-y-12 lg:mt-0 lg:border lg:border-line lg:bg-white lg:px-10 lg:py-8">
          {karteInfo?.parts?.map(item => {
            const {
              partId,
              partName,
              replacementRecommendedMonth,
              replacementDate,
              reserveDisplay,
              alert,
              chubunCd,
              shobunCd,
            } = item;
            return (
              <ul key={partId} className="flex flex-col">
                <li className="flex flex-row items-end justify-between border-b border-line">
                  <div className="flex flex-row items-center">
                    <Icons.Image
                      src={alert === '1' ? Icons.alert : KARTE_ICONS[partName]}
                      className="text-base"
                      alt={partName}
                    />
                    <label className="text-base ml-1 font-bold sm:ml-2">
                      {partName}
                    </label>
                  </div>
                  <Linked
                    text="詳細"
                    isLine={true}
                    linkIcon="R"
                    className="text-sm mb-1 h-fit font-bold"
                    onClick={() => {
                      // GTMカスタムイベントパラメータ
                      const dataLayer = (window.dataLayer =
                        window.dataLayer || []);
                      dataLayer.push({
                        event: KARTE_SHOW_DETAIL_SERVICE,
                        karteShowDetailService: `{partId: ${partId}, partName: ${partName}`,
                      });

                      dispatch(
                        fetchKarteInfoDetail({
                          ...item,
                          cardNumber,
                          selectedSrySeq,
                        })
                      );
                    }}
                  />
                </li>
                <div className="mt-4 flex flex-col flex-wrap items-center justify-between gap-y-4 sm:mt-2 sm:flex-row">
                  <div className="text-sm flex flex-1 flex-col gap-y-2 font-medium">
                    <div className="flex flex-row">
                      <label className="w-32">
                        {partName === '車検' ? '次回車検年月日' : '次回交換推奨月'}
                      </label>
                      <label
                        className={`ml-10 w-36 ${
                          alert === '1' ? 'text-red' : ''
                        }`}
                      >
                        {replacementRecommendedMonth}
                      </label>
                    </div>
                    <div className="flex flex-row">
                      <label className="w-32">
                        {partName === '車検' ? '前回車検日' : '前回交換日'}
                      </label>
                      <label className="ml-10 w-36">{replacementDate}</label>
                    </div>
                  </div>
                  {reserveDisplay === '1' && (
                    <div className="flex flex-1 flex-row justify-end">
                      <AprosButton
                        text="予約へ進む"
                        comClassName="reserve"
                        className="lg:w-[260px]"
                        onClick={() => {
                          // GTMカスタムイベントパラメータ
                          const dataLayer = (window.dataLayer =
                            window.dataLayer || []);
                          dataLayer.push({
                            event: KARTE_SELECT_SERVICE,
                            karteSelectService: `{chubunCd: ${chubunCd}${
                              shobunCd ? `, shobunCd: ${shobunCd}` : ``
                            }}`,
                          });

                          const navToSelect = () => {
                            navigate(Path.RESERVATION_SERVICE_SELECT, {
                              state: {
                                shopCode: accountInfo.shopCode,
                                serviceSelectScreen: Path.KARTE,
                                selectedSrySeq,
                                chubunCd: chubunCd,
                                shobunCd: shobunCd,
                              },
                            });
                          };
                          if (!cautionDisplayDate) {
                            dispatch(displayCautionActions.check(navToSelect));
                          } else {
                            navToSelect();
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </ul>
            );
          })}
        </div>
      </div>
      <NotesModal />
      <KarteDetailModal
        isModalOpen={showDetailModal}
        toggleModal={() => dispatch(actions.closeDetailModal())}
        item={{
          vehicleName: vehicleName || '-',
          ...(karteInfo.parts?.find(part => part.partName === '車検') || {}),
          ...selectedKarteInfo,
          ...karteInfoDetail,
        }}
      />
    </>
  );
};

export default KarteDescription;
